import router from '@/router';
import { Log, UserManager, WebStorageStateStore } from 'oidc-client';
import config from '@/config';
import { auth as authConstants } from '@/store/constants';
import { OIDCErrorCodesThatRequireSignin } from '@/auth/constants';
import { fetchUserInfo } from '@/auth/utils';
import { ADMIN } from 'simulix-shared/auth/roles';

const { UNSET_TOKEN_DATA, SET_TOKEN_DATA } = authConstants.mutations;

export default function AuthAgcoCustomerPortalModule() {
  if (config.env === 'development') {
    Log.logger = console;
    Log.level = Log.DEBUG;
  }

  const userManager = new UserManager({
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    authority: config.auth.authority,
    client_id: config.auth.clientId,
    redirect_uri: `${config.frontendURL}/oauth/silentsignin/`,
    silent_redirect_uri: `${config.frontendURL}/oauth/silentsignin/`,
    response_type: 'token id_token',
    scope: 'openid',
    post_logout_redirect_uri: `${config.frontendURL}/`,
    loadUserInfo: true,
    automaticSilentRenew: true,
    monitorSession: true,
    filterProtocolClaims: true,
    clockSkew: 900,
  });

  const initialState = {
    tokenData: null, // TODO rename to accessToken
  };

  const authGetters = {
    accessToken: (state) => state.tokenData && state.tokenData.access_token,
    user: (state) => state.tokenData && state.tokenData.profile,
    isUserAdmin: (state, getters) => getters.user && getters.user.role === ADMIN,
  };

  const actions = {
    async initialAutoLogIn({ dispatch, state }) {
      if (state.tokenData) return;

      let initialLoading = true;
      dispatch('setLoaderVisiblity', { isVisible: true, content: 'Logging user in...' });

      try {
        userManager.events.addUserLoaded(async (tokenData) => {
          await dispatch('fetchUserInfo', tokenData);

          if (initialLoading) {
            initialLoading = false;
            dispatch('setLoaderVisiblity', { isVisible: false });
          }
        });

        userManager.events.addSilentRenewError(async () => {
          await dispatch('logOut');
        });

        const user = await userManager.getUser();
        if (user && user.expired === false) {
          userManager.events.load(user);
        } else {
          await dispatch('redirectToLoginPage');
        }
      } catch (e) {
        if (OIDCErrorCodesThatRequireSignin.includes(e.error)) {
          await dispatch('redirectToLoginPage');
        } else {
          dispatch('setErrorPageVisiblity', {
            isVisible: true,
            title: 'Log in error.',
            content: 'It was not possible to log in the user. Please try again later.',
          });
        }
      }
    },
    async fetchUserInfo({ commit, dispatch }, tokenData) {
      try {
        // eslint-disable-next-line no-param-reassign
        tokenData.profile = await fetchUserInfo(tokenData.access_token);
        commit(SET_TOKEN_DATA, tokenData);
        dispatch('redirectToDefaultPage');
      } catch (e) {
        dispatch('setErrorPageVisiblity', {
          isVisible: true,
          title: 'Log in error.',
          content: e.message,
        });
      }
    },
    async processOAuthCallback({ dispatch }) {
      dispatch('setLoaderVisiblity', { isVisible: true, content: 'Logging user in...' });

      try {
        const tokenData = await userManager.signinCallback();
        await dispatch('fetchUserInfo', tokenData);
      } catch (err) {
        dispatch('setErrorPageVisiblity', {
          isVisible: true,
          title: 'Log in error.',
          content: 'It was not possible to log in the user. Please try again later.',
        });
      }

      dispatch('setLoaderVisiblity', { isVisible: false });
    },
    async logOut({ commit }) {
      await userManager.removeUser();
      commit(UNSET_TOKEN_DATA);

      if (await userManager.metadataService.getEndSessionEndpoint()) {
        await userManager.signoutRedirect();
      }
    },
    async redirectToLoginPage() {
      if (!router.history.current.meta.publicRoute) {
        await userManager.signinRedirect();
      }
    },
    redirectToDefaultPage({ getters }) {
      const isAdminRoute = router.history.current.matched.some(
        (match) => match.parent && match.parent.name === 'AdminWrapper'
      );

      if (router.history.current.name === 'OAuthSilentSignin') {
        router.replace(router.history.current.query.next || { name: 'ApplicationSelector' });
      }

      if (router.history.current.meta.publicRoute || (isAdminRoute && !getters.isUserAdmin)) {
        router.push(router.history.current.query.next || { name: 'ApplicationSelector' });
      }
    },
  };

  /* eslint-disable no-param-reassign */
  const mutations = {
    [SET_TOKEN_DATA](state, tokenData) {
      state.tokenData = tokenData;
    },
    [UNSET_TOKEN_DATA](state) {
      state.tokenData = null;
    },
  };
  /* eslint-enable no-param-reassign */

  return {
    state: initialState,
    getters: authGetters,
    actions,
    mutations,
  };
}
